import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import Note from "@/components/atoms/Note";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import StoreCompanyCourseNoteModal from "@/components/molecules/modals/company/courses/StoreCompanyCourseNoteModal";
import MyCommissionStats from "@/components/molecules/cards/general/MyCommissionStats";
import GeneralFilters from "@/components/molecules/table-filters/GeneralFilters";
import AllCompaniesCoursesHeader from "@/components/molecules/table-headers/AllCompaniesCoursesHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import CompanyCourse, {
  CompaniesCoursesFilters,
} from "@/interfaces/CompanyCourse";
import { useGetCompaniesCoursesQuery } from "@/services/companies/courses";
import {
  faEye,
  faMessage,
  faUserGraduate,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, useRef } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";

const GeneralView: FC = () => {
  const StoreCompanyCourseNoteRef =
    useRef<ElementRef<typeof StoreCompanyCourseNoteModal>>(null);

  const [state, setState] = useTableState<
    Partial<TableState> & CompaniesCoursesFilters
  >("general", {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data, isLoading, isFetching } = useGetCompaniesCoursesQuery({
    ...state.pagination,
    ...(debouncedQuery && { query: debouncedQuery }),
    ...(state.followUpDate && { followUpDate: state.followUpDate }),
    ...(state.startAt && { startAt: state.startAt }),
    ...(state.endAt && { endAt: state.endAt }),
    ...(state.signatureDate && { signatureDate: state.signatureDate }),
    ...(state.sendingDate && { sendingDate: state.sendingDate }),
    ...(state.agreementDate && { agreementDate: state.agreementDate }),
    ...(state.invoiceDate && { invoiceDate: state.invoiceDate }),
    ...(state.paiementDate && { paiementDate: state.paiementDate }),
    ...(state.status && {
      statusIds: state.status?.map((element) => element.id),
    }),
    ...(state.companies && {
      companyIds: state.companies?.map((element) => element.id),
    }),
    ...(state.sdrs && {
      sdrIds: state.sdrs.map((element) => element.id),
    }),
    ...(state.aes && {
      aeIds: state.aes.map((element) => element.id),
    }),
    ...(state.opcos && {
      opcoIds: state.opcos?.map((element) => element.id),
    }),
    ...(state.organizations && {
      organizationIds: state.organizations?.map((element) => element.id),
    }),
    ...(state.instructors && {
      instructorIds: state.instructors?.map((element) => element.id),
    }),
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const columns = useRef<ColumnDef<CompanyCourse>[]>([
    {
      header: "Actions",
      enableSorting: false,
      enableHiding: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id, companyId, instructorId }) => (
        <div>
          <Link
            to={`/companies/${companyId}/courses/${id}`}
            className="btn btn-info btn-sm"
          >
            <FontAwesomeIcon icon={faEye} />
          </Link>
          {instructorId && (
            <Link
              to={`/instructors/${instructorId}`}
              className="btn btn-success btn-sm ms-2"
            >
              <FontAwesomeIcon icon={faUserGraduate} />
            </Link>
          )}
          <Button
            variant="danger"
            size="sm"
            className="ms-2"
            onClick={() =>
              StoreCompanyCourseNoteRef.current?.showOrHiddenModal([
                companyId,
                id,
              ])
            }
          >
            <FontAwesomeIcon icon={faMessage} />
          </Button>
        </div>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Dernière\xa0note",
      footer: (props) => props.column.id,
      meta: {
        headerProps: { style: { minWidth: "14.625rem" } },
        cellProps: {
          style: {
            maxWidth: 500,
          },
        },
      },
      accessorFn: ({ notes }) =>
        notes?.[0] ? <Note note={notes[0]} /> : <span>Aucune note</span>,
      id: "lastNote",
      cell: (info) => info.getValue(),
    },
    {
      header: "Organisme",
      footer: (props) => props.column.id,
      accessorFn: ({ organisation }) => (
        <SoftBadge
          bg={organisation?.backgroundColor ?? "danger"}
          textColor={organisation?.textColor}
          className="me-2"
        >
          {organisation?.name || "Inconnu"}
        </SoftBadge>
      ),
      id: "organizationId",
      cell: (info) => info.getValue(),
    },
    {
      header: "Opco",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) => (
        <SoftBadge
          bg={company.activitySector?.opco?.backgroundColor ?? "danger"}
          textColor={company.activitySector?.opco?.textColor}
          className="me-2"
        >
          {company.activitySector?.opco?.name || "Inconnu"}
        </SoftBadge>
      ),
      id: "opcoId",
      cell: (info) => info.getValue(),
    },
    {
      header: "Statut",
      footer: (props) => props.column.id,
      accessorFn: ({ status }) => (
        <SoftBadge
          bg={status?.backgroundColor ?? "danger"}
          textColor={status?.textColor}
          className="me-2"
        >
          {status?.name || "Inconnu"}
        </SoftBadge>
      ),
      id: "statusId",
      cell: (info) => info.getValue(),
    },
    {
      header: "Télépro",
      footer: (props) => props.column.id,
      accessorFn: ({ company: { sdr } }) => (
        <SoftBadge bg={sdr ? "info" : "warning"} className="me-2">
          {sdr?.user.fullname ?? "Inconnu"}
        </SoftBadge>
      ),
      id: "sdr",
      cell: (info) => info.getValue(),
    },
    {
      header: "Commercial",
      footer: (props) => props.column.id,
      accessorFn: ({ company: { ae } }) => (
        <SoftBadge bg={ae ? "info" : "warning"} className="me-2">
          {ae?.user.fullname ?? "Inconnu"}
        </SoftBadge>
      ),
      id: "ae",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date\xa0de\xa0signature",
      footer: (props) => props.column.id,
      accessorFn: ({ signatureDate }) =>
        signatureDate
          ? DateTime.fromISO(signatureDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "signatureDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Siret",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) => company.siret,
      id: "siret",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro de dossier final",
      footer: (props) => props.column.id,
      accessorFn: ({ finalFolderNumber }) => finalFolderNumber ?? "Inconnu",
      id: "finalFolderNumber",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom\xa0de\xa0l'entreprise",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) => company.name,
      id: "companyName",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom\xa0de\xa0la\xa0formation",
      footer: (props) => props.column.id,
      accessorFn: ({ course }) => course?.name ?? "Inconnu",
      id: "courseName",
      cell: (info) => info.getValue(),
    },
    {
      header: "ID\xa0Opco",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) => company.opcoIdentifier,
      id: "opcoIdentifier",
      cell: (info) => info.getValue(),
    },
    {
      header: "Interlocuteur",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) =>
        formatInterlocutor(
          company.interlocutorFirstname,
          company.interlocutorLastname
        ),
      id: "interlocutor",
      cell: (info) => info.getValue(),
    },
    {
      header: "Adresse",
      footer: (props) => props.column.id,
      accessorFn: ({ company, address }) =>
        address || company.address || "Inconnu",
      id: "address",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro\xa0de\xa0téléphone\xa0de\xa0l'entreprise",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) =>
        company.phone ? <PhoneCallLink number={company.phone} /> : "Inconnu",
      id: "companyPhone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro\xa0de\xa0téléphone\xa0fixe\xa0de\xa0l'entreprise",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) =>
        company.fixPhone ? <PhoneCallLink number={company.fixPhone} /> : "Inconnu",
      id: "companyFixPhone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Email de l'entreprise",
      footer: (props) => props.column.id,
      accessorFn: ({ company }) => company.email ?? "Inconnu",
      id: "companyEmail",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de relance controle",
      footer: (props) => props.column.id,
      accessorFn: ({ followUpDate }) =>
        followUpDate
          ? DateTime.fromISO(followUpDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "followUpDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de début de formation",
      footer: (props) => props.column.id,
      accessorFn: ({ startAt }) =>
        startAt ? DateTime.fromISO(startAt).toFormat("dd-MM-yyyy") : "Inconnu",
      id: "startAt",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de fin de formation",
      footer: (props) => props.column.id,
      accessorFn: ({ endAt }) =>
        endAt ? DateTime.fromISO(endAt).toFormat("dd-MM-yyyy") : "Inconnu",
      id: "endAt",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nombre d'apprenants",
      footer: (props) => props.column.id,
      accessorFn: ({ peopleCount, peopleNumber }) =>
        peopleCount ?? peopleNumber,
      id: "peopleCount",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nombre d'heures",
      footer: (props) => props.column.id,
      accessorFn: ({ hoursNumber }) => `${hoursNumber} heures`,
      id: "hoursNumber",
      cell: (info) => info.getValue(),
    },
    {
      header: "Montant demandé",
      footer: (props) => props.column.id,
      accessorFn: ({ askingPrice }) => `${askingPrice} €`,
      id: "askingPrice",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date d'envoi",
      footer: (props) => props.column.id,
      accessorFn: ({ sendingDate }) =>
        sendingDate
          ? DateTime.fromISO(sendingDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "sendingDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de sessions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ sessionStartDate, sessionEndDate }) =>
        sessionEndDate && sessionStartDate
          ? `${DateTime.fromISO(sessionStartDate).toFormat(
              "dd-MM-yyyy",
            )} au ${DateTime.fromISO(sessionEndDate).toFormat("dd-MM-yyyy")}`
          : "Inconnu",
      id: "sessionsDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date d'accord",
      footer: (props) => props.column.id,
      accessorFn: ({ agreementDate }) =>
        agreementDate
          ? DateTime.fromISO(agreementDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "agreementDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Montant accordé",
      footer: (props) => props.column.id,
      accessorFn: ({ agreementAmount }) =>
        agreementAmount ? `${agreementAmount} €` : "Inconnu",
      id: "agreementAmount",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de facture",
      footer: (props) => props.column.id,
      accessorFn: ({ invoiceDate }) =>
        invoiceDate
          ? DateTime.fromISO(invoiceDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "invoiceDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de paiement",
      footer: (props) => props.column.id,
      accessorFn: ({ paiementDate }) =>
        paiementDate
          ? DateTime.fromISO(paiementDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "paiementDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorId, instructor }) => (
        <Link to={`/instructors/${instructorId}`}>{instructor?.fullname}</Link>
      ),
      id: "instructor",
      cell: (info) => info.getValue(),
    },
    {
      header: "Prix du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorPriceHt }) =>
        instructorPriceHt ? `${instructorPriceHt} €` : "Inconnu",
      id: "instructorPriceHt",
      cell: (info) => info.getValue(),
    },
    {
      header: "Montant de la facture du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorTotalPriceHt }) =>
        instructorTotalPriceHt ? `${instructorTotalPriceHt} € HT` : "Inconnu",
      id: "instructorTotalPriceHt",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro de facture du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorInvoiceNumber }) => instructorInvoiceNumber,
      id: "instructorInvoiceNumber",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de facture du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorInvoiceDate }) =>
        instructorInvoiceDate
          ? DateTime.fromISO(instructorInvoiceDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "instructorInvoiceDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de paiement du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorInvoicePaiementDate }) =>
        instructorInvoicePaiementDate
          ? DateTime.fromISO(instructorInvoicePaiementDate).toFormat(
              "dd-MM-yyyy",
            )
          : "Inconnu",
      id: "instructorInvoicePaiementDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de contrat du formateur",
      footer: (props) => props.column.id,
      accessorFn: ({ instructorContractDate }) =>
        instructorContractDate
          ? DateTime.fromISO(instructorContractDate).toFormat("dd-MM-yyyy")
          : "Inconnu",
      id: "instructorContractDate",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: ({ createdAt }) =>
        DateTime.fromISO(createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <StoreCompanyCourseNoteModal ref={StoreCompanyCourseNoteRef} />
      <MyCommissionStats />
      <Table
        resource="company-courses"
        HeaderComponent={AllCompaniesCoursesHeader}
        FilterComponent={GeneralFilters}
        smallFilters
        tableName="general"
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading || isFetching}
      />
    </div>
  );
};

export default GeneralView;
